/**
 * <trnr-faq-search> for the live TRNR FAQ page (sections/trnr-faq.liquid).
 * Ported 24 Oct 2026 from Grok test assets/s-faq-search.js: client-side search over this page's
 * Q&As only (never Shopify search), CSS Custom Highlight API with <mark> fallback, polite live
 * region, Esc / clear button reset, plus a 250ms height animation for the accordions that is
 * skipped under prefers-reduced-motion. Without JS the search stays hidden and <details> work natively.
 */
const HIGHLIGHT = 'trnr-faq-match';
const fold = (s) => s.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();

class TrnrFaqSearch extends HTMLElement {
  #ac;
  #timer;
  #input;
  #clear;
  #status;
  #empty;
  #items = [];
  #groups = [];
  #snapshot = null;
  #marks = [];

  connectedCallback() {
    this.#ac = new AbortController();
    const signal = this.#ac.signal;
    this.#input = this.querySelector('[data-faq-input]');
    if (!this.#input) return;
    this.#clear = this.querySelector('[data-faq-clear]');
    this.#status = this.querySelector('[data-faq-status]');
    this.#empty = this.querySelector('[data-faq-empty]');
    this.#items = [...this.querySelectorAll('[data-faq-item]')].map((el) => ({
      el,
      text: fold(el.textContent),
    }));
    this.#groups = [...this.querySelectorAll('[data-faq-group]')].map((el) => ({
      el,
      items: this.#items.filter((item) => el.contains(item.el)),
      nav: this.querySelector(`[data-faq-nav-for="${el.id}"]`),
    }));
    this.querySelector('[data-faq-search]')?.removeAttribute('hidden');

    this.#input.addEventListener('input', () => {
      clearTimeout(this.#timer);
      this.#timer = setTimeout(() => this.#run(), 180);
    }, { signal });
    this.#input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.#input.value) {
        event.preventDefault();
        this.#reset();
      }
    }, { signal });
    this.#clear?.addEventListener('click', () => {
      this.#reset();
      this.#input.focus();
    }, { signal });
    this.#setupAccordions(signal);

    if (this.#input.value) this.#run();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    clearTimeout(this.#timer);
    this.#clearHighlights();
  }

  // Fitking-style open/close: 250ms height tween on each <details>; answers fade in via CSS.
  // Skipped when the visitor prefers reduced motion (native instant toggle).
  #setupAccordions(signal) {
    const reduce = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)');
    for (const { el } of this.#items) {
      const summary = el.querySelector('summary');
      if (!summary || !el.animate) continue;
      summary.addEventListener('click', (event) => {
        if (reduce?.matches) return;
        event.preventDefault();
        el._faqAnim?.cancel();
        const start = el.offsetHeight;
        const opening = !el.open;
        if (opening) el.open = true;
        const end = opening ? el.scrollHeight : summary.offsetHeight;
        el.style.overflow = 'hidden';
        const anim = el.animate({ height: [`${start}px`, `${end}px`] }, { duration: 250, easing: 'cubic-bezier(.2,0,0,1)' });
        el._faqAnim = anim;
        anim.onfinish = () => {
          if (!opening) el.open = false;
          el.style.overflow = '';
          el._faqAnim = null;
        };
        anim.oncancel = () => { el.style.overflow = ''; };
      }, { signal });
    }
  }

  #reset() {
    clearTimeout(this.#timer);
    this.#input.value = '';
    this.#run();
  }

  #run() {
    const raw = this.#input.value.trim();
    const terms = [...new Set(fold(raw).split(/\s+/).filter(Boolean))];
    this.#clearHighlights();
    this.#clear.hidden = raw === '';

    if (terms.length === 0) {
      for (const { el } of this.#items) el.hidden = false;
      for (const group of this.#groups) this.#toggleGroup(group, true);
      if (this.#snapshot) {
        this.#snapshot.forEach((open, el) => (el.open = open));
        this.#snapshot = null;
      }
      this.#empty.hidden = true;
      this.#status.textContent = '';
      return;
    }

    this.#snapshot ??= new Map(this.#items.map(({ el }) => [el, el.open]));
    let count = 0;
    for (const item of this.#items) {
      const match = terms.every((term) => item.text.includes(term));
      item.el.hidden = !match;
      if (match) {
        count += 1;
        item.el.open = true;
        this.#highlight(item.el, terms);
      }
    }
    for (const group of this.#groups) this.#toggleGroup(group, group.items.some(({ el }) => !el.hidden));
    this.#empty.hidden = count > 0;
    const template = count === 1 ? this.dataset.resultsOne : this.dataset.resultsOther;
    this.#status.textContent = (template || '').replace('[count]', String(count)).replace('[query]', raw);
  }

  #toggleGroup(group, visible) {
    group.el.hidden = !visible;
    if (group.nav) group.nav.hidden = !visible;
  }

  #highlight(root, terms) {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const hits = [];
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
      const text = fold(node.data);
      // NFD folding can change length; only highlight nodes where offsets still line up.
      if (text.length !== node.data.length) continue;
      const spans = [];
      for (const term of terms) {
        for (let i = text.indexOf(term); i !== -1; i = text.indexOf(term, i + term.length)) spans.push([i, i + term.length]);
      }
      if (spans.length === 0) continue;
      // Merge overlapping spans ("ship" inside "shipping") so each character is marked once.
      spans.sort((a, b) => a[0] - b[0]);
      const merged = [spans[0]];
      for (const [s, e] of spans.slice(1)) {
        const last = merged[merged.length - 1];
        if (s <= last[1]) last[1] = Math.max(last[1], e);
        else merged.push([s, e]);
      }
      hits.push({ node, spans: merged });
    }
    if (hits.length === 0) return;

    if (globalThis.CSS?.highlights && globalThis.Highlight) {
      const highlight = CSS.highlights.get(HIGHLIGHT) ?? new Highlight();
      for (const { node, spans } of hits) {
        for (const [s, e] of spans) {
          const range = new Range();
          range.setStart(node, s);
          range.setEnd(node, e);
          highlight.add(range);
        }
      }
      CSS.highlights.set(HIGHLIGHT, highlight);
      return;
    }
    // Fallback: wrap each node's spans from the end so earlier offsets stay valid.
    for (const { node, spans } of hits) {
      for (const [s, e] of spans.reverse()) {
        const range = new Range();
        range.setStart(node, s);
        range.setEnd(node, e);
        const mark = document.createElement('mark');
        range.surroundContents(mark);
        this.#marks.push(mark);
      }
    }
  }

  #clearHighlights() {
    CSS.highlights?.delete(HIGHLIGHT);
    for (const mark of this.#marks) {
      const parent = mark.parentNode;
      if (!parent) continue;
      mark.replaceWith(...mark.childNodes);
      parent.normalize();
    }
    this.#marks = [];
  }
}

if (!customElements.get('trnr-faq-search')) customElements.define('trnr-faq-search', TrnrFaqSearch);
